export interface Category {
  key: string;
  name: string;
  icon: string;
  desc: string;
}

export interface ContentBlock {
  type: 'p' | 'quote' | 'img';
  text?: string;
  src?: string;
  caption?: string;
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  cover: string;
  category: string;
  author: string;
  date: string;
  tags: string[];
  readTime: number;
  views: number;
  content: ContentBlock[];
}

export interface HeroSlide {
  id: string;
  articleId: string;
  title: string;
  summary: string;
  image: string;
  tag: string;
}

export const categories: Category[] = [
  { key: 'current-affairs', name: '时事', icon: 'ri-newspaper-line', desc: '聚焦城市发展与民生政策，记录正在发生的时代变化。' },
  { key: 'tech', name: '科技', icon: 'ri-cpu-line', desc: '追踪前沿技术、数码产品与产业创新，洞察科技新趋势。' },
  { key: 'life', name: '生活', icon: 'ri-leaf-line', desc: '分享旅行、健康、美食与家居灵感，让日常更有质感。' },
  { key: 'culture', name: '文化', icon: 'ri-book-open-line', desc: '走近博物馆、非遗、阅读与传统，感受人文的温度。' },
  { key: 'entertainment', name: '娱乐', icon: 'ri-film-line', desc: '影视、音乐与综艺的观察与解读，发现好内容。' },
];

export const heroSlides: HeroSlide[] = [
  {
    id: 'slide-1',
    articleId: 'tech-1',
    title: '国产大模型迭代加速，多模态能力成新焦点',
    summary: '从文本到图像、语音与视频，多模态大模型正快速走进日常工作与生活场景。',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20futuristic%20technology%20background%20with%20glowing%20teal%20and%20orange%20gradient%20particles%2C%20flowing%20digital%20network%20lines%2C%20clean%20minimal%20composition%2C%20high%20detail%2C%20cinematic%20lighting&width=1600&height=900&seq=lualu-hero-tech-01&orientation=landscape',
    tag: '科技前沿',
  },
  {
    id: 'slide-2',
    articleId: 'cul-1',
    title: '博物馆夜游成新风尚，文化消费持续升温',
    summary: '越来越多年轻人走进夜间开放的博物馆，用一场展览结束忙碌的一天。',
    image: 'https://readdy.ai/api/search-image?query=Warm%20evening%20museum%20hall%20with%20soft%20amber%20lighting%2C%20artistic%20abstract%20cultural%20atmosphere%2C%20elegant%20minimal%20interior%2C%20muted%20teal%20tones%2C%20editorial%20photography%2C%20cinematic&width=1600&height=900&seq=lualu-hero-culture-01&orientation=landscape',
    tag: '文化观察',
  },
  {
    id: 'slide-3',
    articleId: 'life-1',
    title: '春季踏青指南：五个小众目的地推荐',
    summary: '避开人潮，去那些安静又美好的地方，把整个春天装进行囊。',
    image: 'https://readdy.ai/api/search-image?query=Serene%20spring%20landscape%20with%20soft%20green%20hills%20and%20blooming%20flowers%2C%20gentle%20morning%20mist%2C%20airy%20minimal%20nature%20scene%2C%20warm%20natural%20light%2C%20editorial%20photography&width=1600&height=900&seq=lualu-hero-life-01&orientation=landscape',
    tag: '生活方式',
  },
  {
    id: 'slide-4',
    articleId: 'ca-1',
    title: '城市轨道交通新线开通，绿色出行再升级',
    summary: '多条新线路投入运营，通勤更高效，也让城市的绿色脉络更加畅通。',
    image: 'https://readdy.ai/api/search-image?query=Modern%20city%20skyline%20at%20soft%20dawn%20with%20clean%20geometric%20architecture%2C%20teal%20and%20warm%20orange%20tone%2C%20minimal%20urban%20scene%2C%20abstract%20editorial%20photography&width=1600&height=900&seq=lualu-hero-news-01&orientation=landscape',
    tag: '时事热点',
  },
];

export const articles: Article[] = [
  {
    id: 'ca-1',
    title: '城市轨道交通新线开通，绿色出行再升级',
    summary: '多条地铁新线路投入运营，中心城区与新兴片区被更紧密地串联在一起，通勤时间明显缩短。',
    cover: 'https://readdy.ai/api/search-image?query=Modern%20metro%20station%20platform%20with%20clean%20teal%20accent%20lighting%2C%20minimal%20architectural%20lines%2C%20bright%20airy%20interior%2C%20editorial%20photography%2C%20soft%20natural%20light&width=800&height=520&seq=lualu-art-ca1&orientation=landscape',
    category: 'current-affairs',
    author: '鲁啊鲁编辑部',
    date: '2026-09-28',
    tags: ['民生', '出行', '城市'],
    readTime: 5,
    views: 12840,
    content: [
      { type: 'p', text: '随着城市轨道网络的持续扩展，多条新线路于本月正式投入运营。新开通的线路有效串联起中心商业区、科教园区与多个新兴居住片区，为市民提供了更加便捷的出行选择。' },
      { type: 'p', text: '据运营方介绍，新线路在设计阶段就充分考虑了换乘效率，多个站点实现了与既有线路的同站台换乘。早高峰期间，部分通勤走廊的出行时间预计可缩短近三成。' },
      { type: 'quote', text: '绿色出行不是一句口号，而是每天都能被感受到的便利。' },
      { type: 'p', text: '除了运力提升，新站点在无障碍设施、母婴室与智能导乘方面也做了更多细节优化，让老人、儿童与行动不便的乘客都能更从容地使用轨道交通。' },
    ],
  },
  {
    id: 'ca-2',
    title: '多地推进社区养老服务体系建设',
    summary: '从助餐点到日间照料中心，社区正在成为老年人身边最近的养老支持网络。',
    cover: 'https://readdy.ai/api/search-image?query=Bright%20warm%20community%20service%20center%20interior%20with%20soft%20natural%20light%2C%20minimal%20clean%20design%2C%20teal%20and%20orange%20accents%2C%20editorial%20photography&width=800&height=520&seq=lualu-art-ca2&orientation=landscape',
    category: 'current-affairs',
    author: '林知远',
    date: '2026-09-25',
    tags: ['民生', '养老', '政策'],
    readTime: 6,
    views: 9640,
    content: [
      { type: 'p', text: '近期，多地陆续出台社区养老服务提升方案，围绕助餐、助浴、康复护理与精神文化需求，构建“一刻钟”养老服务圈。' },
      { type: 'p', text: '不少社区将闲置空间改造成日间照料中心，引入专业机构提供健康监测与康复训练，同时为白天无人照护的老人提供安全、温暖的去处。' },
      { type: 'quote', text: '让养老服务离老人更近一点，也让子女更安心一点。' },
      { type: 'p', text: '业内人士认为，社区养老的关键在于可持续的运营机制与稳定的专业队伍，未来还需在服务标准与人才培养上持续投入。' },
    ],
  },
  {
    id: 'ca-3',
    title: '春季招聘市场回暖，新兴产业岗位需求攀升',
    summary: '新能源、人工智能与高端制造领域的用人需求增长明显，灵活就业形态也在持续丰富。',
    cover: 'https://readdy.ai/api/search-image?query=Modern%20bright%20office%20coworking%20space%20with%20plants%20and%20minimal%20desks%2C%20soft%20daylight%2C%20clean%20editorial%20business%20photography%2C%20teal%20accents&width=800&height=520&seq=lualu-art-ca3&orientation=landscape',
    category: 'current-affairs',
    author: '周慕白',
    date: '2026-09-22',
    tags: ['就业', '经济', '产业'],
    readTime: 4,
    views: 8230,
    content: [
      { type: 'p', text: '多项招聘平台数据显示，本季度招聘市场整体回暖，其中新能源、人工智能、高端制造等新兴产业的岗位需求增长尤为明显。' },
      { type: 'p', text: '与往年相比，企业对复合型人才的偏好更加突出，既懂技术又理解业务的候选人更受青睐，技能培训与职业教育的衔接也成为热门话题。' },
      { type: 'p', text: '与此同时，灵活就业与远程协作岗位持续增加，为求职者提供了更多元的选择，也对个人自我管理能力提出了新的要求。' },
    ],
  },
  {
    id: 'ca-4',
    title: '数字政务平台上线，便民服务一网通办',
    summary: '越来越多高频事项实现线上办理，从证明材料到办事进度，都能在手机上一目了然。',
    cover: 'https://readdy.ai/api/search-image?query=Clean%20minimal%20digital%20government%20service%20concept%20with%20abstract%20interface%20shapes%2C%20teal%20and%20warm%20orange%20gradient%2C%20soft%20light%2C%20editorial%20illustration&width=800&height=520&seq=lualu-art-ca4&orientation=landscape',
    category: 'current-affairs',
    author: '鲁啊鲁编辑部',
    date: '2026-09-19',
    tags: ['政务', '数字化', '便民'],
    readTime: 4,
    views: 7120,
    content: [
      { type: 'p', text: '新版数字政务平台近日正式上线，整合了社保、公积金、户籍等高频服务事项，市民无需在多个窗口之间来回奔波。' },
      { type: 'p', text: '平台引入了统一的身份认证与进度查询功能，办件状态实时可查，材料清单也在办理前清晰展示，减少了反复补交材料的麻烦。' },
      { type: 'quote', text: '把复杂留给自己，把简单留给用户。' },
      { type: 'p', text: '相关负责人表示，后续还将持续优化适老化设计，为不熟悉智能手机的群体保留线下渠道与人工协助。' },
    ],
  },
  {
    id: 'tech-1',
    title: '国产大模型迭代加速，多模态能力成新焦点',
    summary: '从文本到图像、语音与视频，多模态大模型正快速走进日常工作与生活场景。',
    cover: 'https://readdy.ai/api/search-image?query=Abstract%20AI%20neural%20network%20visualization%20with%20glowing%20teal%20nodes%20and%20soft%20orange%20highlights%2C%20dark%20clean%20background%2C%20minimal%20futuristic%20style&width=800&height=520&seq=lualu-art-tech1&orientation=landscape',
    category: 'tech',
    author: '陈砚舟',
    date: '2026-09-30',
    tags: ['人工智能', '科技前沿', '大模型'],
    readTime: 7,
    views: 18760,
    content: [
      { type: 'p', text: '近一段时间，国产大模型迎来密集迭代，多家团队相继发布新版本，多模态能力成为竞争的新焦点。' },
      { type: 'p', text: '与早期只处理文本的模型不同，新一代模型可以同时理解图像、语音与视频，并能完成跨模态的内容生成，为内容创作、客服与教育等场景带来新的可能。' },
      { type: 'quote', text: '真正的进步，不是模型更“聪明”，而是它更懂普通人的需求。' },
      { type: 'p', text: '业内专家提醒，多模态能力的落地仍需关注成本、隐私与内容安全，如何在效率与责任之间取得平衡，将是长期课题。' },
    ],
  },
  {
    id: 'tech-2',
    title: '折叠屏手机进入轻薄时代，用户体验全面升级',
    summary: '更轻的机身、更浅的折痕与更耐用的铰链，让折叠屏从尝鲜走向日常主力机。',
    cover: 'https://readdy.ai/api/search-image?query=Minimal%20product%20photo%20of%20a%20sleek%20foldable%20smartphone%20on%20soft%20neutral%20surface%2C%20clean%20studio%20lighting%2C%20teal%20accent%20reflection%2C%20editorial%20tech%20photography&width=800&height=520&seq=lualu-art-tech2&orientation=landscape',
    category: 'tech',
    author: '苏阑',
    date: '2026-09-27',
    tags: ['数码', '手机', '评测'],
    readTime: 6,
    views: 14320,
    content: [
      { type: 'p', text: '折叠屏手机在经历几代产品迭代后，正逐步解决早期“厚重”“折痕明显”等痛点，机身厚度与重量已接近直板旗舰。' },
      { type: 'p', text: '新一代铰链采用更精密的机械结构，开合手感更顺滑，屏幕折痕在日常使用中几乎难以察觉，耐用性测试标准也明显提高。' },
      { type: 'p', text: '随着分屏多任务、外屏快捷操作等软件体验不断完善，折叠屏在办公与娱乐场景中的价值开始真正释放。' },
    ],
  },
  {
    id: 'tech-3',
    title: '智能家居生态融合，全屋互联走入寻常家庭',
    summary: '不同品牌之间的互联互通正在改善，一句话就能控制灯光、窗帘与家电。',
    cover: 'https://readdy.ai/api/search-image?query=Cozy%20modern%20smart%20living%20room%20with%20soft%20warm%20lighting%2C%20minimal%20scandinavian%20interior%2C%20teal%20and%20orange%20accents%2C%20clean%20editorial%20photography&width=800&height=520&seq=lualu-art-tech3&orientation=landscape',
    category: 'tech',
    author: '许澈',
    date: '2026-09-24',
    tags: ['智能家居', '物联网', '数码'],
    readTime: 5,
    views: 11980,
    content: [
      { type: 'p', text: '智能家居长期面临“生态割裂”的问题，不同品牌设备难以协同。随着统一互联标准的推进，这一局面正在改变。' },
      { type: 'p', text: '如今，用户可以在一个应用里统一管理灯光、窗帘、空调与安防设备，也能通过语音完成场景切换，回家、离家、观影模式一键触达。' },
      { type: 'quote', text: '最好的科技，是让你几乎感觉不到它的存在。' },
      { type: 'p', text: '业内人士建议，选购智能设备时应优先考虑兼容性与数据安全，避免被单一生态过度绑定。' },
    ],
  },
  {
    id: 'tech-4',
    title: '固态电池技术取得突破，续航焦虑有望缓解',
    summary: '更高的能量密度与更好的安全性，让新一代电池成为新能源汽车的关键变量。',
    cover: 'https://readdy.ai/api/search-image?query=Futuristic%20electric%20vehicle%20battery%20cell%20concept%20with%20glowing%20teal%20energy%20flow%2C%20clean%20minimal%20studio%20background%2C%20editorial%20technology%20visual&width=800&height=520&seq=lualu-art-tech4&orientation=landscape',
    category: 'tech',
    author: '陈砚舟',
    date: '2026-09-21',
    tags: ['新能源', '电池', '汽车'],
    readTime: 6,
    views: 10450,
    content: [
      { type: 'p', text: '固态电池被视为下一代动力电池的重要方向。近期，多家研究机构与企业在电解质材料与界面工艺上取得进展。' },
      { type: 'p', text: '相比传统液态电池，固态电池在能量密度与安全性方面具备潜在优势，有望在同等体积下提供更长续航，并降低热失控风险。' },
      { type: 'p', text: '不过，从实验室到大规模量产仍有距离，成本控制与产业链配套将是接下来需要攻克的难点。' },
    ],
  },
  {
    id: 'life-1',
    title: '春季踏青指南：五个小众目的地推荐',
    summary: '避开人潮，去那些安静又美好的地方，把整个春天装进行囊。',
    cover: 'https://readdy.ai/api/search-image?query=Serene%20spring%20meadow%20with%20wild%20flowers%20and%20soft%20morning%20light%2C%20distant%20gentle%20hills%2C%20airy%20minimal%20nature%20photography%2C%20fresh%20green%20tones&width=800&height=520&seq=lualu-art-life1&orientation=landscape',
    category: 'life',
    author: '顾清和',
    date: '2026-09-29',
    tags: ['旅行', '生活', '春季'],
    readTime: 5,
    views: 17620,
    content: [
      { type: 'p', text: '春天最适合出发。与其挤在热门景区，不如把目光投向那些安静的小众目的地，收获一份难得的从容。' },
      { type: 'p', text: '本篇整理了几个适合短途踏青的去处：有被花海包围的山谷小镇，也有沿溪而建的步道村落，交通便利却游人稀少。' },
      { type: 'quote', text: '旅行的意义，不在于走了多远，而在于心是否慢了下来。' },
      { type: 'p', text: '出发前建议提前查好花期与天气，携带轻便的野餐用品，同时注意保护环境，让美景留给下一位到访者。' },
    ],
  },
  {
    id: 'life-2',
    title: '极简生活的十个实用小习惯',
    summary: '从整理桌面到减少无效信息，小改变也能带来轻盈的日常。',
    cover: 'https://readdy.ai/api/search-image?query=Minimal%20bright%20desk%20with%20a%20plant%2C%20notebook%20and%20cup%20of%20tea%2C%20clean%20scandinavian%20style%2C%20soft%20natural%20light%2C%20calm%20editorial%20photography&width=800&height=520&seq=lualu-art-life2&orientation=landscape',
    category: 'life',
    author: '叶宁',
    date: '2026-09-26',
    tags: ['生活', '习惯', '整理'],
    readTime: 4,
    views: 13870,
    content: [
      { type: 'p', text: '极简并不是把东西全部丢掉，而是让每一件留在身边的事物都真正被需要。' },
      { type: 'p', text: '从每天花五分钟整理桌面开始，到定期清理手机里不再使用的应用，再到学会对不必要的邀约说不，改变可以很微小，却能持续累积。' },
      { type: 'p', text: '坚持一段时间后，你会发现注意力更集中，情绪也更稳定，生活反而变得更丰富。' },
    ],
  },
  {
    id: 'life-3',
    title: '家庭收纳改造：让空间翻倍的收纳思路',
    summary: '不用大动干戈，几个收纳逻辑就能让家里的每个角落井井有条。',
    cover: 'https://readdy.ai/api/search-image?query=Tidy%20minimal%20home%20storage%20shelves%20with%20woven%20baskets%20and%20folded%20textiles%2C%20warm%20neutral%20tones%2C%20clean%20editorial%20interior%20photography&width=800&height=520&seq=lualu-art-life3&orientation=landscape',
    category: 'life',
    author: '顾清和',
    date: '2026-09-23',
    tags: ['家居', '收纳', '生活'],
    readTime: 5,
    views: 11040,
    content: [
      { type: 'p', text: '收纳的核心不是“装得多”，而是“找得到、放得回”。先按使用频率分区，再为每类物品确定固定位置，效率会大幅提升。' },
      { type: 'p', text: '垂直空间常常被忽视。利用墙面、柜门内侧与床下空间，可以释放出意想不到的容量。' },
      { type: 'quote', text: '好的收纳，是让物品各归其位，也让人各得其所。' },
      { type: 'p', text: '最后，尽量选择统一色系的收纳容器，视觉上更整洁，也更容易长期维持。' },
    ],
  },
  {
    id: 'life-4',
    title: '周末厨房：三十分钟搞定一桌家常菜',
    summary: '不必是高手，掌握几个小技巧，也能让平凡的周末变得有滋有味。',
    cover: 'https://readdy.ai/api/search-image?query=Home%20cooked%20colorful%20family%20dishes%20on%20a%20warm%20wooden%20table%2C%20soft%20natural%20light%2C%20appetizing%20clean%20food%20photography%2C%20cozy%20atmosphere&width=800&height=520&seq=lualu-art-life4&orientation=landscape',
    category: 'life',
    author: '叶宁',
    date: '2026-09-20',
    tags: ['美食', '生活', '厨房'],
    readTime: 4,
    views: 9560,
    content: [
      { type: 'p', text: '忙碌了一周，周末最治愈的事，莫过于亲手做一桌热气腾腾的家常菜。' },
      { type: 'p', text: '诀窍在于“先准备、后开火”：提前把食材洗净切好，按烹饪顺序摆盘，真正下锅时就从容许多。' },
      { type: 'p', text: '一荤一素一汤的组合最省心，调味不必复杂，突出食材本身的味道就足够温暖。' },
    ],
  },
  {
    id: 'cul-1',
    title: '博物馆夜游成新风尚，文化消费持续升温',
    summary: '越来越多年轻人走进夜间开放的博物馆，用一场展览结束忙碌的一天。',
    cover: 'https://readdy.ai/api/search-image?query=Elegant%20museum%20gallery%20at%20night%20with%20warm%20amber%20spotlights%20and%20minimal%20art%20displays%2C%20refined%20cultural%20atmosphere%2C%20cinematic%20editorial%20photography&width=800&height=520&seq=lualu-art-cul1&orientation=landscape',
    category: 'culture',
    author: '沈砚青',
    date: '2026-09-28',
    tags: ['博物馆', '文化', '消费'],
    readTime: 5,
    views: 12430,
    content: [
      { type: 'p', text: '夜间的博物馆，多了一份安静与神秘。近期，多地博物馆推出延时开放，让上班族也能在下班后走进展厅。' },
      { type: 'p', text: '配合夜游，不少场馆还推出了小型讲座、手作体验与文创市集，把单一的观展变成更丰富的文化体验。' },
      { type: 'quote', text: '文化不是高高在上的展品，而是能被触摸和感受的生活。' },
      { type: 'p', text: '专家认为，夜间文化场所有效延长了消费时间，也为城市夜经济注入了更多文化底色。' },
    ],
  },
  {
    id: 'cul-2',
    title: '非遗手艺走进校园，传统技艺焕发新生',
    summary: '当剪纸、扎染与传统木作遇上年轻的手，古老技艺有了新的表达。',
    cover: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20handicraft%20workspace%20with%20colorful%20tie%20dye%20textiles%20and%20tools%2C%20warm%20natural%20light%2C%20artisanal%20editorial%20photography%2C%20soft%20tones&width=800&height=520&seq=lualu-art-cul2&orientation=landscape',
    category: 'culture',
    author: '沈砚青',
    date: '2026-09-25',
    tags: ['非遗', '文化', '教育'],
    readTime: 5,
    views: 10210,
    content: [
      { type: 'p', text: '近些年，越来越多的非遗项目以社团、选修课的形式走进校园，让学生亲手体验传统技艺的魅力。' },
      { type: 'p', text: '在老师的指导下，孩子们把剪纸纹样用在自己的设计里，把扎染的蓝白配色穿在身上，古老技艺因此有了年轻的模样。' },
      { type: 'p', text: '非遗传承人表示，让技艺“活”起来，关键是要让它与当下生活产生连接，而不只是被陈列在展柜里。' },
    ],
  },
  {
    id: 'cul-3',
    title: '城市书店转型：从卖书到卖生活方式',
    summary: '书店不再只是买书的地方，咖啡、展览与社群活动让它成为城市的会客厅。',
    cover: 'https://readdy.ai/api/search-image?query=Warm%20inviting%20modern%20bookstore%20interior%20with%20wooden%20shelves%20and%20reading%20corners%2C%20soft%20ambient%20lighting%2C%20cozy%20editorial%20photography%2C%20teal%20accents&width=800&height=520&seq=lualu-art-cul3&orientation=landscape',
    category: 'culture',
    author: '林知远',
    date: '2026-09-22',
    tags: ['阅读', '书店', '文化'],
    readTime: 5,
    views: 8890,
    content: [
      { type: 'p', text: '面对线上购书的冲击，越来越多的城市书店选择转型，把自己打造成融合阅读、咖啡与活动的复合空间。' },
      { type: 'p', text: '在这些空间里，书架之外还有展览、讲座与读书会，人们因为共同的兴趣聚集在一起，书店也因此成为社区的会客厅。' },
      { type: 'quote', text: '书店卖的不只是书，更是一种愿意慢下来的生活方式。' },
      { type: 'p', text: '业内人士认为，讲好“人”的故事、经营好社群关系，是实体书店长期立足的关键。' },
    ],
  },
  {
    id: 'cul-4',
    title: '古籍数字化保护，让千年文明触手可及',
    summary: '高清扫描与全文检索技术，让沉睡的古籍走进更多人的屏幕。',
    cover: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20book%20pages%20with%20calligraphy%20illuminated%20by%20soft%20light%2C%20minimal%20elegant%20composition%2C%20warm%20paper%20tones%2C%20editorial%20photography&width=800&height=520&seq=lualu-art-cul4&orientation=landscape',
    category: 'culture',
    author: '沈砚青',
    date: '2026-09-19',
    tags: ['古籍', '数字化', '文化'],
    readTime: 5,
    views: 7640,
    content: [
      { type: 'p', text: '古籍是文明的载体，但纸张脆弱、流传有限。数字化保护让这些珍贵文献得以被更长久地保存和更广泛地使用。' },
      { type: 'p', text: '借助高清扫描与全文检索技术，研究者可以快速定位所需内容，普通读者也能在屏幕上近距离欣赏千年前的笔墨。' },
      { type: 'p', text: '专家指出，数字化只是第一步，如何让古籍内容被更好地理解和传播，还需要更多生动有趣的解读方式。' },
    ],
  },
  {
    id: 'ent-1',
    title: '暑期档电影市场观察：多元题材同台竞技',
    summary: '现实题材与类型片各有所长，观众的选择比以往更加丰富。',
    cover: 'https://readdy.ai/api/search-image?query=Abstract%20cinema%20theme%20with%20soft%20warm%20spotlight%20and%20minimal%20theater%20seats%2C%20teal%20and%20orange%20tones%2C%20clean%20editorial%20visual&width=800&height=520&seq=lualu-art-ent1&orientation=landscape',
    category: 'entertainment',
    author: '温以宁',
    date: '2026-09-30',
    tags: ['电影', '影视', '观察'],
    readTime: 6,
    views: 15210,
    content: [
      { type: 'p', text: '今年的暑期档呈现出鲜明的多元化特征，现实题材、动画与类型片各有亮点，观众有了更多选择。' },
      { type: 'p', text: '值得关注的是，观众对口碑的敏感度持续上升，映后讨论与社交平台的评价，正在成为影响观影决策的重要因素。' },
      { type: 'quote', text: '好内容从不缺少观众，缺少的是被看见的机会。' },
      { type: 'p', text: '业内普遍认为，稳定的内容质量与清晰的类型定位，将帮助更多作品在激烈竞争中找到自己的观众。' },
    ],
  },
  {
    id: 'ent-2',
    title: '音乐节经济火爆，年轻人为热爱买单',
    summary: '从城市到乡野，音乐节成为年轻人感受现场与社交的新方式。',
    cover: 'https://readdy.ai/api/search-image?query=Vibrant%20outdoor%20music%20festival%20stage%20at%20dusk%20with%20warm%20lights%20and%20crowd%2C%20abstract%20energetic%20atmosphere%2C%20teal%20and%20orange%20tones%2C%20editorial%20photography&width=800&height=520&seq=lualu-art-ent2&orientation=landscape',
    category: 'entertainment',
    author: '温以宁',
    date: '2026-09-27',
    tags: ['音乐', '演出', '年轻人'],
    readTime: 5,
    views: 13120,
    content: [
      { type: 'p', text: '音乐节正成为年轻人重要的线下娱乐方式。一场音乐节不仅带来演出，也带动了周边餐饮、住宿与旅行的消费。' },
      { type: 'p', text: '与单纯的观演不同，音乐节更强调氛围与社交，人们在这里结识朋友，也释放压力。' },
      { type: 'p', text: '不过，票价、交通与现场体验管理也成为被频繁讨论的问题，主办方需要在规模与品质之间找到平衡。' },
    ],
  },
  {
    id: 'ent-3',
    title: '短剧市场快速扩张，内容精品化成趋势',
    summary: '节奏快、篇幅短的微短剧受到欢迎，行业开始从量走向质。',
    cover: 'https://readdy.ai/api/search-image?query=Minimal%20vertical%20screen%20media%20concept%20with%20soft%20gradient%20lighting%2C%20abstract%20digital%20content%20theme%2C%20teal%20and%20warm%20orange%20tones%2C%20clean%20editorial%20visual&width=800&height=520&seq=lualu-art-ent3&orientation=landscape',
    category: 'entertainment',
    author: '周慕白',
    date: '2026-09-24',
    tags: ['短剧', '影视', '趋势'],
    readTime: 5,
    views: 11870,
    content: [
      { type: 'p', text: '微短剧凭借紧凑的节奏和适配碎片时间的特点，在近几年快速扩张，成为内容行业不可忽视的一股力量。' },
      { type: 'p', text: '随着竞争加剧，单纯依靠套路的内容逐渐失去吸引力，制作方开始更加重视剧本打磨与演员表现。' },
      { type: 'quote', text: '再短的剧，也要讲一个值得被记住的故事。' },
      { type: 'p', text: '业内认为，精品化与题材多元化将是短剧下一阶段的主要方向。' },
    ],
  },
  {
    id: 'ent-4',
    title: '综艺节目新趋势：慢生活题材受追捧',
    summary: '告别强冲突，轻松治愈的慢综艺让观众在忙碌中找到喘息。',
    cover: 'https://readdy.ai/api/search-image?query=Cozy%20slow%20living%20theme%20with%20warm%20sunlit%20room%20and%20plants%2C%20minimal%20calm%20interior%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20teal%20accents&width=800&height=520&seq=lualu-art-ent4&orientation=landscape',
    category: 'entertainment',
    author: '温以宁',
    date: '2026-09-21',
    tags: ['综艺', '慢生活', '观察'],
    readTime: 4,
    views: 9240,
    content: [
      { type: 'p', text: '相比节奏紧张、冲突强烈的节目，越来越多观众开始偏爱节奏舒缓的慢综艺。' },
      { type: 'p', text: '这类节目把镜头对准日常、自然与人际间的真诚互动，让观众在忙碌之余获得放松与治愈。' },
      { type: 'p', text: '内容创作者表示，慢综艺看似简单，实则对情感真实度要求更高，唯有真诚才能打动人心。' },
    ],
  },
];